/**
 * What is left of the row helpers once the API answers for itself.
 *
 * This file used to reconstruct, per row, what the backend now states
 * outright — which candidate a row points at (walking `chosenRank` over
 * `alternatives`), what its percentage was (falling back through similarity
 * and distance), whether it was an AI proposal (sniffing metadata), what a
 * metadata bag held. All of that is now a typed field on `row.target` /
 * `row.source`, resolved server-side by the one function every backend read
 * path shares.
 *
 * What survives is the one thing the client still decides: how a pending,
 * unsaved edit changes what the row shows before the server has confirmed it.
 */

import { ReviewStatus, type RowEdit } from "@models/job";
import type {
  Candidate,
  MappingResultRow,
  MappingResultTarget,
} from "@models/mappingResult";

/** Placeholder rendered when a cell has no value. */
export const PLACEHOLDER = "-";

/** Maps a review status to the colour of the small status dot. */
export const STATUS_DOT_COLOR: Readonly<Record<ReviewStatus, string>> = {
  [ReviewStatus.Pending]: "gray",
  [ReviewStatus.Accepted]: "teal",
  [ReviewStatus.Rejected]: "red",
  [ReviewStatus.Corrected]: "blue",
  [ReviewStatus.Failed]: "red",
};

/** The status a row renders as, including an edit not yet saved. */
export const resolveReviewStatusOf = (
  row: MappingResultRow,
  edit: RowEdit | undefined,
): ReviewStatus => edit?.reviewStatus ?? row.reviewStatus;

/**
 * The rank the row points at, including an edit the reviewer has made but not
 * yet saved.
 *
 * The server already resolved the SAVED pick into `row.target`, and
 * `target.rank` says which candidate that is. This only covers the gap before
 * the PATCH lands, so the row and the dropdown move on click rather than a
 * round trip later.
 *
 * An accept edit that names no rank SEALS the stored pick — mirroring the
 * backend, where an accept without `chosen_rank` preserves it. A correction
 * always names one (the type requires it).
 */
export const resolveChosenRank = (
  row: MappingResultRow,
  edit: RowEdit | undefined,
): number | null => {
  if (resolveReviewStatusOf(row, edit) === ReviewStatus.Rejected) return null;
  return edit?.chosenRank ?? row.chosenRank ?? row.target?.rank ?? null;
};

/**
 * The candidate dropdown, with the current one marked.
 *
 * Every entry the server sent is kept, in the order it sent them — rank 1
 * included. That is not a detail: after a correction, rank 1 is the pipeline's
 * own top match and picking it back is how the correction is undone. Slicing
 * it off (or capping the list to a client-side number) silently removes the
 * undo, and hides candidates the backend deliberately served.
 *
 * `isCurrent` is computed here, once, so no component compares a rank to `1`.
 * That comparison is what conflated "the first candidate" with "the row's
 * answer", and the two stop being the same thing the moment anyone corrects a
 * row.
 */
export const buildCandidates = <T extends MappingResultTarget>(
  row: MappingResultRow,
  edit: RowEdit | undefined,
  alternatives: readonly T[] | undefined,
): Candidate<T>[] => {
  if (!alternatives) return [];
  const currentRank = resolveChosenRank(row, edit);
  return alternatives.map((target) => ({
    target,
    isCurrent: currentRank != null && target.rank === currentRank,
  }));
};

/**
 * The target a row displays, honouring an unsaved edit.
 *
 * `row.target` is the server's answer and is right for everything saved. Two
 * cases override it client-side: a rejected row shows no target, and a row
 * just corrected onto another candidate shows that candidate — possible only
 * while its dropdown is open, since that is when the candidates are in memory.
 */
export const resolveDisplayTarget = <T extends MappingResultTarget>(
  row: MappingResultRow & { target: T | null },
  edit: RowEdit | undefined,
  alternatives: readonly T[] | undefined,
): T | null => {
  if (resolveReviewStatusOf(row, edit) === ReviewStatus.Rejected) return null;
  const rank = resolveChosenRank(row, edit);
  if (rank == null || rank === row.target?.rank) return row.target;
  return alternatives?.find((c) => c.rank === rank) ?? row.target;
};
